// Shared blocks for the community sections (Gallery, Marketplace): cards, price, rating, likes, node chain.
import { html, toast } from '../lib/dom';
import { icon } from './icons';
import { thumb, coverThumb } from './thumb';
import { avatar, status } from './ui';
import * as store from '../store/ui';
import { getCreator, getListing, isForSale, nodeTypeByKey, bumpLikes, LISTING_STATUS_LABEL } from '../api/marketplace';
import { money, num, initials, relTime } from '../lib/format';

export const priceTag = (price?: any) => html`<span class="price ${price ? '' : 'free'}">${price ? money(price, 0) : 'Free'}</span>`;

export function stars(rating?: any) {
  const n = Math.round(rating || 0);
  return html`<span class="stars" aria-label="${(rating || 0).toFixed(1)} out of 5">${'★'.repeat(n)}<span class="off">${'★'.repeat(5 - n)}</span></span>`;
}

const STATUS_CLASS: any = { published: 'completed', in_review: 'review', draft: 'draft', rejected: 'failed', unlisted: 'archived' };
export const listingStatus = (l?: any) => status(STATUS_CLASS[l.status] || 'draft', LISTING_STATUS_LABEL[l.status]);

export function creatorLine(c?: any, { handle = true }: any = {}) {
  return html`<span class="row creator-line" style="gap:6px;min-width:0">${avatar(c, 'sm')}<span class="ellipsis">${handle ? `@${c.handle}` : c.name}</span>${c.verified ? html`<span class="verified" title="Verified creator">${icon('check')}</span>` : ''}</span>`;
}

export function likeButton(it?: any) {
  const on = store.isLiked(it.id);
  return html`<button class="like-btn ${on ? 'on' : ''}" data-like="${it.id}" aria-pressed="${String(on)}" aria-label="${on ? 'Unlike' : 'Like'}">${icon(on ? 'heartFill' : 'heart')}<span>${num(it.likes)}</span></button>`;
}

export function workCard(it?: any) {
  const c = getCreator(it.creator_id);
  const l = it.listing_id ? getListing(it.listing_id) : null;
  return html`<a class="card hover work-card" href="#/gallery/${it.id}">
    <div class="work-thumb" style="aspect-ratio:${it.aspect}">
      ${thumb(it.thumbnail, { label: it.title })}
      ${it.kind === 'video' ? html`<span class="kind-badge">${icon('play')}</span>` : ''}
      ${it.featured ? html`<span class="corner-badge">Featured</span>` : ''}
    </div>
    <div class="body">
      <div class="title ellipsis">${it.title}</div>
      <div class="row" style="gap:8px">${creatorLine(c)}<span class="spacer"></span>${likeButton(it)}</div>
      ${isForSale(l) ? html`<div class="made-with"><span class="ellipsis">${icon('tag')}${l.title}</span>${priceTag(l.price)}</div>` : ''}
    </div>
  </a>`;
}

export function listingCard(l?: any) {
  const c = getCreator(l.seller_id);
  return html`<a class="card hover entity-card listing-card" href="#/marketplace/${l.id}">
    <div class="listing-cover">${coverThumb(l)}${!isForSale(l) ? html`<span class="corner-badge dark">${LISTING_STATUS_LABEL[l.status]}</span>` : ''}</div>
    <div class="body" style="gap:8px">
      <div class="row"><span class="type-tag muted">${l.category}</span><span class="spacer"></span>${priceTag(l.price)}</div>
      <div class="title" style="font-size:15px">${l.title}</div>
      <p class="muted clamp-2" style="font-size:12.5px;line-height:1.45">${l.tagline}</p>
      <div class="row" style="gap:6px">${creatorLine(c, { handle: false })}</div>
      <div class="meta">${l.rating ? html`${stars(l.rating)}<span>${l.rating.toFixed(1)} (${num(l.reviews_count)})</span><span>·</span>` : ''}<span>${num(l.sales_count)} ${l.price ? 'sales' : 'installs'}</span><span>·</span><span>${l.node_count} nodes</span></div>
    </div>
  </a>`;
}

export function chainPreview(chain?: any) {
  return html`<div class="chain">${chain.map((step?: any, i?: any) => {
    const t = nodeTypeByKey(step.type_key);
    return html`${i ? html`<span class="chain-arrow">→</span>` : ''}<span class="chain-step" style="--role:var(--role-${t?.role || 'tool'})"><span class="chain-name">${step.name}</span><span class="chain-type">${step.type_key}</span></span>`;
  })}</div>`;
}

export function reviewRow(r?: any) {
  return html`<div class="review">
    <span class="avatar" style="background:hsl(${r.avatar_hue} 38% 38%)">${initials(r.author_name)}</span>
    <div class="col" style="gap:4px"><div class="row wrap" style="gap:8px"><strong style="font-weight:500">${r.author_name}</strong>${stars(r.rating)}<span class="mono muted" style="font-size:11px">${relTime(r.created_at)}</span></div><p style="font-size:14px">${r.text}</p></div>
  </div>`;
}

// Likes and follows are per-browser UI state; counts are adjusted in memory so the page reflects the click.
export function bindCommunity(root?: any, ctx?: any) {
  root.addEventListener('click', (e?: any) => {
    const like = e.target.closest('[data-like]');
    if (like) {
      e.preventDefault(); e.stopPropagation();
      const on = store.toggleLike(like.dataset.like);
      bumpLikes(like.dataset.like, on ? 1 : -1);
      ctx.rerender();
      return;
    }
    const follow = e.target.closest('[data-follow]');
    if (follow) {
      e.preventDefault();
      const on = store.toggleFollow(follow.dataset.follow);
      toast(on ? `Following @${getCreator(follow.dataset.follow).handle}` : 'Unfollowed');
      ctx.rerender();
    }
  });
}
